import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, createTextVNode as _createTextVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-c07004ce"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["selected"]
const _hoisted_2 = ["disabled"]
const _hoisted_3 = ["disabled"]
const _hoisted_4 = ["src", "srcset", "sizes"]
const _hoisted_5 = { class: "person-card-body" }
const _hoisted_6 = { class: "title-area" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = { class: "title" }
const _hoisted_9 = {
  key: 2,
  class: "subtitle"
}
const _hoisted_10 = {
  key: 0,
  class: "lifespan"
}
const _hoisted_11 = {
  key: 1,
  class: "profiles"
}
const _hoisted_12 = { key: 1 }

import { computed, PropType } from 'vue'
import { ContentDataAltKey, Gender, getContentDataAltSize, genderImages, dottedPersonImage, Person, ViewPerson } from '@/rd/ResearchDataModel'
import { usePersonStore } from '@/rd/PersonStore'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useContentStore } from '@/rd/ContentStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useUserStore } from '@/gp/UserStore'
import { useExploreStore } from '@/explore/ExploreStore'
import { CompositeId } from '@/rd/CompositeId'
import { LoadMode } from '@/util/AsyncData'
import { ProfileUtils, ProfileType } from './profiles/ProfileUtils'
import { LinkIcon, LockIcon } from '@zhuowenli/vue-feather-icons'
import UserLink from '@/manage/users/UserLink.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'PersonCard',
  props: {
  personId: String,
  layout: String as PropType<"full" | "small" | "tiny" | "dot">,
  disabled: Boolean,
  selectMode: Boolean,
  selectEmpty: Boolean,
  static: Boolean,
  selectedId: String,
  highlightMode: Boolean,
  highlighted: Boolean,
  dimmed: Boolean,
  raised: Boolean,
  showProfileType: Boolean,
  showProfileCount: String as PropType<'always' | 'multiple'>,
  showRestricted: Boolean,
  noHover: Boolean,
},
  emits: [
  'click',
  'clickImage',
  'showProfiles',
  'update:selectedId',
],
  setup(__props, { emit }) {

const props = __props;





const viewPersonStore = useViewPersonStore()
const personStore = usePersonStore()
const contentStore = useContentStore()
const dataGroupStore = useDataGroupStore()
const userStore = useUserStore()
const exploreStore = useExploreStore()

const layoutRef = computed(() => props.layout ?? 'full')
const viewPersonRef = computed(() => viewPersonStore.getAsyncPerson(props.personId, LoadMode.EnsureLoaded)?.data)
const personRef = computed(() => isSingletonRef.value ? personStore.getAsyncPerson(props.personId, LoadMode.EnsureLoaded)?.data : undefined)
const displayPropsRef = computed(() => viewPersonRef.value?.displayProperties)
const cardTitleRef = computed(() => 
  !props.personId ? '(none)' :
  isDanglingRef.value ? '(deleted or not visible)' : 
  displayPropsRef.value?.displayName ?? "(no name)"
)

const dataGroupRef = computed(() => isSingletonRef.value ? dataGroupStore.getAsyncGroup(CompositeId.getGroupId(props.personId), LoadMode.EnsureLoaded)?.data : undefined)
const ownerRef = computed(() => userStore.getAsyncUser(dataGroupRef.value?.ownerId, LoadMode.EnsureLoaded)?.data)
const profileTypeRef = computed(() => ProfileUtils.getProfileType(personRef.value, dataGroupRef.value, ownerRef.value))
const profileTypeTextRef = computed(() => ProfileUtils.getProfileSubtitle(profileTypeRef.value))
const showProfileTypeRef = computed(() => isSingletonRef.value && props.showProfileType)

const cardClassRef = computed(() => ({ 
  'disabled': props.disabled,
  'highlight': props.highlightMode && props.highlighted,
  'dimmed': props.dimmed || (props.highlightMode && !props.highlighted),
  'raised': props.raised,
  ['card-' + layoutRef.value]: true,
  'restricted': personRef.value?.restricted,
  'empty': !props.personId,
  'dangling': isDanglingRef.value,
  'show-hover': isInteractiveRef.value && !props.noHover && !props.disabled,
}))
const genderClassRef = computed(() => [displayPropsRef.value?.gender?.toLowerCase()])
const personImageUrlRef = computed(() => displayPropsRef.value?.primaryPhotoSignedUrl?.signedUrl)
const placeholderImageUrlRef = computed(() => 
  personRef.value?.restricted ? dottedPersonImage :
  profileCountRef.value == 0 ? dottedPersonImage :
  genderImages[displayPropsRef.value?.gender ?? "default"])
const personImageSrcRef = computed(() => personImageUrlRef.value ?? placeholderImageUrlRef.value)
const personImageContentRef = computed(() => {
  if (props.layout == 'full') {
    const contentId = displayPropsRef.value?.primaryPhotoSignedUrl?.contentId
    return contentStore.getContents([contentId], LoadMode.EnsureLoaded).at(0)
  }
  return undefined
})
const personImageAltKeys = [ContentDataAltKey.Gallery, ContentDataAltKey.Thumb]
const personImageSrcsetRef = computed(() => {
  const signedUrls = personImageContentRef.value?.dataSignedUrls
  if (signedUrls) {
    const orig = signedUrls.find(u => !u.altKey)
    const gal = signedUrls.find(u => u.altKey == ContentDataAltKey.Gallery)
    const thu = signedUrls.find(u => u.altKey == ContentDataAltKey.Thumb)
    const sources: string[] = []
    if (thu) {
      sources.push(`${thu.signedUrl} ${getContentDataAltSize(ContentDataAltKey.Thumb)}w`)
    }
    if (gal || orig) {
      const useGal = (gal ?? orig)!
      sources.push(`${useGal.signedUrl} ${getContentDataAltSize(ContentDataAltKey.Gallery)}w`)
    }
    return sources.join(', ')
  }
  return undefined
})
const personImageSizesRef = computed(() => {
  switch (layoutRef.value) {
    case 'full': return "(min-width: 768px) 100px, 60px" // from CSS above
    case 'small': return "36px"
    default: return undefined
  }
})

const showLifespanRef = computed(() => layoutRef.value == 'full' || !(showProfileCountRef.value || showProfileTypeRef.value))
const profileIdsRef = computed(() => {
  if (!props.personId)
    return []

  const matchIds = viewPersonRef.value?.matchIds ?? []
  const matches = personStore.getLoadedPersonList(matchIds)
  return matches.filter(p => (p.profile && !p.placeholder) || (!p.profile && (props.showRestricted || !p.restricted)))
    .map(p => p.id!)
})
const profileCountRef = computed(() => profileIdsRef.value.length ?? 0)
const showProfileCountRef = computed(() => 
  props.showProfileCount == 'always' || 
  (props.showProfileCount == 'multiple' && profileCountRef.value > 1))
const isDanglingRef = computed(() => props.personId && profileCountRef.value == 0)
const isSingletonRef = computed(() => profileCountRef.value == 1)
const isEmptyRef = computed(() => profileCountRef.value == 0)
const profilesTextRef = computed(() => profileCountRef.value == 1 ? "profile" : "profiles")

const selectModeRef = computed(() => !props.static && props.selectMode && (props.selectEmpty || !isEmptyRef.value) && !props.disabled)
const linkModeRef = computed(() => !props.static && !props.selectMode && !isDanglingRef.value && !isEmptyRef.value && !props.disabled)
const isInteractiveRef = computed(() => selectModeRef.value || linkModeRef.value)
const linkUrlRef = computed(() => ViewPerson.getPageUrl(props.personId ?? ''))
const useCardButtonRef = computed(() => props.layout != 'full')

const selectedRef = computed({
  get: () => props.selectMode && props.selectedId == props.personId,
  set: (value) => {
    emit('update:selectedId', value ? props.personId : undefined) // toggle selection of this item
  }
})

function onClick() {
  // do this even if no personId specified
  emit('click', props.personId) // for stateless selection
  selectedRef.value = false // for stateful selection (click deselects, state can only be set by parent)
}

function onClickImage() {
  const contentId = personRef.value?.displayProperties.primaryPhotoSignedUrl?.contentId
  if (contentId) {
    emit('clickImage', contentId)
  }
}

function onShowProfiles() {
  emit('showProfiles')
}

function setStartPerson() {
  if (props.personId) {
    exploreStore.setStartPersonId(props.personId)
  }
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["person-card", cardClassRef.value]),
    selected: selectedRef.value
  }, [
    (useCardButtonRef.value && selectModeRef.value)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          type: "button",
          class: "overlay",
          disabled: __props.disabled,
          onClick: onClick
        }, null, 8, _hoisted_2))
      : _createCommentVNode("", true),
    (useCardButtonRef.value && linkModeRef.value)
      ? (_openBlock(), _createBlock(_component_router_link, {
          key: 1,
          class: "overlay",
          disabled: __props.disabled,
          to: linkUrlRef.value
        }, null, 8, ["disabled", "to"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["gender-bar", genderClassRef.value])
    }, null, 2),
    _createElementVNode("button", {
      type: "button",
      class: _normalizeClass(["btn btn-unstyled person-card-image", { 'placeholder': !personImageUrlRef.value }]),
      disabled: !isInteractiveRef.value,
      onClick: onClickImage
    }, [
      _createElementVNode("img", {
        src: personImageSrcRef.value,
        srcset: personImageSrcsetRef.value,
        sizes: personImageSizesRef.value
      }, null, 8, _hoisted_4)
    ], 10, _hoisted_3),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        (!useCardButtonRef.value && selectModeRef.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              class: "overlay",
              disabled: __props.disabled,
              onClick: onClick
            }, null, 8, _hoisted_7))
          : _createCommentVNode("", true),
        (!useCardButtonRef.value && linkModeRef.value)
          ? (_openBlock(), _createBlock(_component_router_link, {
              key: 1,
              class: "overlay",
              disabled: __props.disabled,
              to: linkUrlRef.value
            }, null, 8, ["disabled", "to"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_8, _toDisplayString(cardTitleRef.value), 1),
        (showProfileTypeRef.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              (profileTypeRef.value == _unref(ProfileType).ManagedLinked)
                ? (_openBlock(), _createBlock(_unref(LinkIcon), {
                    key: 0,
                    size: "1x"
                  }))
                : _createCommentVNode("", true),
              (personRef.value?.restricted)
                ? (_openBlock(), _createBlock(_unref(LockIcon), {
                    key: 1,
                    size: "1x"
                  }))
                : _createCommentVNode("", true),
              _createTextVNode(" " + _toDisplayString(profileTypeTextRef.value) + " ", 1),
              (ownerRef.value)
                ? (_openBlock(), _createBlock(UserLink, {
                    key: 2,
                    userId: ownerRef.value?.id,
                    dimmed: ""
                  }, null, 8, ["userId"]))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      (showLifespanRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(displayPropsRef.value?.lifespanText), 1))
        : _createCommentVNode("", true),
      (showProfileCountRef.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            (profileCountRef.value && !props.static)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  class: "btn btn-link btn-inline link-secondary",
                  onClick: onShowProfiles
                }, _toDisplayString(profileCountRef.value) + " " + _toDisplayString(profilesTextRef.value), 1))
              : (profileCountRef.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(profileCountRef.value) + " " + _toDisplayString(profilesTextRef.value), 1))
                : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ])
  ], 10, _hoisted_1))
}
}

})